import {
  splitRideOrderId,
  toMemberImageUrl,
} from '@component/DriverChat/helpers';
import {
  ChatMessage,
  ChatPreview,
  ChatSender,
  DeliveryState,
  DriverChatSocketEvent,
  DriverConversation,
} from '@component/DriverChat/types';

type ScheduledTime = { hour: number; minute: number } | string | null;

export type ConversationDTO = {
  conversationId: number;
  rideSegmentId: number;
  status: string;
  driver?: { id?: string | null; name?: string; initials?: string } | null;
  vehicle?: { description?: string; plate?: string } | null;
  confirmationCode?: string | null;
  member?: {
    id?: number | string | null;
    fullName?: string | null;
    image?: string | null;
  } | null;
  ride?: {
    status?: string;
    statusId?: number;
    // list shape
    pickupAddress?: string;
    pickupTime?: string;
    dropoffAddress?: string;
    dropoffTime?: string;
    // detail / open shape
    pickup?: { address?: string; scheduledTime?: ScheduledTime };
    dropoff?: { address?: string; scheduledTime?: ScheduledTime };
  } | null;
  lastMessage?: {
    senderType: string;
    message: string;
    createdAt: string;
  } | null;
  unreadCount?: number;
};

export type MessageDTO = {
  id: number;
  conversationId: number;
  clientMessageId?: string | null;
  senderType: string;
  senderName?: string | null;
  messageType?: string | null;
  message?: string | null;
  rideStatusId?: number | null;
  displayText?: string | null;
  status?: string | null;
  errorCode?: number | null;
  createdAt: string;
};

/**
 * The backend sends UTC timestamps, sometimes without an offset
 * ("2026-10-07T08:20:03.0480182"), which the browser would read as local time.
 * Treat an offset-less value as UTC (same rule as formatUTCToLocal) and keep
 * three fractional digits so every browser parses it; display then uses the
 * user's own time zone.
 */
export const toUtcIso = (value: string): string => {
  if (!value) return value;
  const trimmed = value.replace(/(\.\d{3})\d+/, '$1');
  return /(Z|[+-]\d{2}:?\d{2})$/i.test(trimmed) ? trimmed : `${trimmed}Z`;
};

const pad = (n: number) => String(n).padStart(2, '0');

export const formatScheduledTime = (
  time?: ScheduledTime
): string | undefined => {
  if (!time) return undefined;
  if (typeof time === 'string') return time.slice(0, 5);
  return `${pad(time.hour)}:${pad(time.minute)}`;
};

const toSender = (value?: string | null): ChatSender => {
  const upper = (value ?? '').toUpperCase();
  if (upper === 'DRIVER' || upper === 'SYSTEM') return upper;
  return 'DISPATCHER';
};

const DELIVERY_BY_STATUS: Record<string, DeliveryState> = {
  PENDING: 'sending',
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read',
  FAILED: 'failed',
  UNDELIVERED: 'failed',
};

export const toDeliveryState = (status?: string | null): DeliveryState =>
  DELIVERY_BY_STATUS[(status ?? '').toUpperCase()] ?? 'sent';

export const toChatMessage = (dto: MessageDTO): ChatMessage => {
  const sender = toSender(dto.senderType);
  return {
    id: String(dto.id),
    clientMessageId: dto.clientMessageId ?? undefined,
    conversationId: dto.conversationId,
    sender,
    text:
      sender === 'SYSTEM'
        ? (dto.displayText ?? dto.message ?? '')
        : (dto.message ?? dto.displayText ?? ''),
    createdAt: toUtcIso(dto.createdAt),
    // Status only drives receipts on the dispatcher's own messages.
    status: sender === 'DISPATCHER' ? toDeliveryState(dto.status) : undefined,
    senderName: dto.senderName ?? undefined,
    statusId: dto.rideStatusId ?? undefined,
    errorCode: dto.errorCode ?? null,
  };
};

/** Oldest first, regardless of the order the server returns. */
export const toChatMessages = (dtos: MessageDTO[] = []): ChatMessage[] =>
  dtos
    .map(toChatMessage)
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() ||
        Number(a.id) - Number(b.id)
    );

const toPreview = (last: ConversationDTO['lastMessage']): ChatPreview | null =>
  last
    ? {
        sender: toSender(last.senderType),
        text: last.message,
        createdAt: toUtcIso(last.createdAt),
      }
    : null;

export const toDriverConversation = (
  dto: ConversationDTO
): DriverConversation => {
  const name = dto.driver?.name ?? '';
  const ride = dto.ride ?? {};
  const { confirmationCode, segmentNumber } = splitRideOrderId(
    dto.confirmationCode
  );
  const [memberFirstName, ...memberRest] = (dto.member?.fullName ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return {
    conversationId: dto.conversationId,
    conversationStatus: dto.status,
    segmentId: dto.rideSegmentId,
    // The backend does not return the ride id; the entry button's seed supplies it.
    rideId: null,
    driverFirstName: name.split(/\s+/)[0] ?? '',
    driverInitials: dto.driver?.initials,
    memberFirstName,
    memberLastName: memberRest.join(' ') || undefined,
    memberImage: toMemberImageUrl(dto.member?.image),
    confirmationCode,
    segmentNumber,
    vehicleLabel: dto.vehicle?.description,
    licensePlate: dto.vehicle?.plate,
    statusId: ride.statusId ?? null,
    pickupAddress: ride.pickup?.address ?? ride.pickupAddress,
    pickupTime:
      formatScheduledTime(ride.pickup?.scheduledTime) ??
      formatScheduledTime(ride.pickupTime),
    dropOffAddress: ride.dropoff?.address ?? ride.dropoffAddress,
    dropOffTime:
      formatScheduledTime(ride.dropoff?.scheduledTime) ??
      formatScheduledTime(ride.dropoffTime),
    lastMessage: toPreview(dto.lastMessage),
    unreadCount: dto.unreadCount ?? 0,
  };
};

const isMessageDTO = (value: any): value is MessageDTO =>
  !!value && value.id != null && value.senderType != null;

const toNumber = (value: unknown): number | null =>
  value == null || Number.isNaN(Number(value)) ? null : Number(value);

/**
 * STOMP envelopes are `{ type, conversationId, ...}`; message events carry
 * the message DTO either nested (`message` / `payload`) or inline.
 * Returns null for anything the chat does not handle (e.g. unread-count pushes,
 * which the UI reads over REST instead).
 */
export const toSocketEvent = (raw: any): DriverChatSocketEvent | null => {
  if (!raw?.type) return null;
  const nested = [raw.message, raw.payload, raw.data].find(isMessageDTO);
  const dto: MessageDTO | undefined =
    nested ?? (isMessageDTO(raw) ? raw : undefined);
  const conversationId = toNumber(raw.conversationId ?? dto?.conversationId);

  switch (raw.type) {
    case 'CHAT_MESSAGE_CREATED':
      return dto && conversationId != null
        ? {
            type: 'message',
            conversationId,
            message: toChatMessage({ ...dto, conversationId }),
          }
        : null;
    case 'CHAT_MESSAGE_STATUS_UPDATED':
      return dto && conversationId != null
        ? {
            type: 'delivery',
            conversationId,
            messageId: String(dto.id),
            status: toDeliveryState(dto.status),
          }
        : null;
    case 'CHAT_MESSAGES_READ':
      return conversationId != null
        ? {
            type: 'read',
            conversationId,
            lastReadMessageId: toNumber(raw.lastReadMessageId),
          }
        : null;
    case 'CHAT_NEW_MESSAGE_NOTIFICATION':
      return {
        type: 'notification',
        conversationId,
        segmentId: toNumber(raw.rideSegmentId ?? raw.segmentId),
        ...splitRideOrderId(
          typeof raw.confirmationCode === 'string' ? raw.confirmationCode : null
        ),
      };
    case 'RIDE_STATUS_CHANGED':
      return {
        type: 'rideStatus',
        conversationId,
        message:
          dto && conversationId != null
            ? toChatMessage({ ...dto, conversationId })
            : undefined,
      };
    default:
      return null;
  }
};
